import * as React from 'react';
import { useParams } from "react-router-dom-v5-compat"

import IndividualStudentPracticeCard from './individual_student_practice_card'
import IndividualStudentDiagnosticCard from './individual_student_diagnostic_card'
import IndividualStudentRecommendationCard from './individual_student_recommendation_card'

import EmptyState from '../empty_state'
import { generateRecommendations, proficientDiagnostic, DIAGNOSTIC_LEVELS, helpImg, studentHistoryHasPreDiagnostic, NO_STUDENT_ID, diagnosticTypeOptions, } from '../shared'
import { SequencePracticeInterface, StudentHistoryInterface, ClassroomInterface, LeveledAssignmentsInterface, DiagnosticsAssignedInterface, } from '../interfaces'
import { Spinner, Tooltip, titleCase, EXTRA_SMALL_ICON_BASE_SRC, SMALL_ICON_BASE_SRC, Snackbar, defaultSnackbarTimeout, } from '../../../../../Shared/index'
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor'

const clockIcon = <img alt="" src={`${SMALL_ICON_BASE_SRC}/clock.svg`} />
const thumbsUpIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/thumbs-up.svg`} />
const thumbsDownIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/thumbs-down.svg`} />
const starIcon = <img alt="" src={`${SMALL_ICON_BASE_SRC}/star.svg`} />

interface IndividualStudentProps {
  classroom: ClassroomInterface;
  useELLDiagnostics: boolean;
  sequencePractice: SequencePracticeInterface;
  showEmptyState: boolean;
  studentHistory: StudentHistoryInterface,
  timeframe: string,
  teacherClassroomIds: number[];
  getPreviouslyAssignedDiagnosticDataForClassroom: () => void;
  previouslyAssignedDiagnosticsByLevel: DiagnosticsAssignedInterface;
}

const IndividualStudent = ({ classroom, getPreviouslyAssignedDiagnosticDataForClassroom, previouslyAssignedDiagnosticsByLevel, useELLDiagnostics, sequencePractice, studentHistory, timeframe, teacherClassroomIds, showEmptyState, }: IndividualStudentProps) => {
  const [recommendations, setRecommendations] = React.useState<LeveledAssignmentsInterface>(null)
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarText, setSnackbarText] = React.useState('');

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const { studentId, } = useParams()

  React.useEffect(() => {
    getPreviouslyAssignedDiagnosticDataForClassroom()
  }, [classroom.id])

  React.useEffect(() => {
    if (!studentHistory) { return }

    const recommendedDiagnostics = generateRecommendations(useELLDiagnostics, [studentHistory], classroom.id)
    setRecommendations(recommendedDiagnostics)
  }, [studentHistory])

  if (studentId === NO_STUDENT_ID) {
    return (
      <EmptyState
        bodyText="Please add students to this class. Once they complete an assessment, this report will become available, enabling you to track their progress and view recommendations for the next diagnostic to assign."
        ctaLink="/teachers/classrooms"
        ctaText="Manage classes"
        headerText="No diagnostic history or recommendations are available"
      />
    )
  }

  if (showEmptyState) {
    return (
      <EmptyState
        bodyText="There is no diagnostic history available for the filters selected, so no recommendation can be provided. Consider modifying a filter or assigning a diagnostic to this student. Once they complete an assessment, this report will become available."
        headerText="No diagnostic history or recommendations are available for this student"
      />
    )
  }

  if (
    !studentHistory ||
    !recommendations ||
    !previouslyAssignedDiagnosticsByLevel
  ) {
    return (
      <div className="diagnostic-insights-content recommendations by-class">
        <Spinner />
      </div>
    )
  }

  const proficientOnEveryDiagnostic = DIAGNOSTIC_LEVELS.every(diagnosticLevel => {
    const studentLearningSequences = studentHistory.student_learning_sequences[diagnosticLevel]
    return proficientDiagnostic(studentLearningSequences)
  })

  const firstName = () => studentHistory.user_name.split(' ')[0]

  const renderRecommendationCard = (diagnosticLevel) => {
    return (
      <IndividualStudentRecommendationCard
        classroomId={classroom.id}
        diagnosticLevel={diagnosticLevel}
        firstName={firstName()}
        getPreviouslyAssignedDiagnosticDataForClassroom={getPreviouslyAssignedDiagnosticDataForClassroom}
        key={diagnosticLevel}
        previouslyAssignedDiagnosticsByLevel={previouslyAssignedDiagnosticsByLevel}
        recommendations={recommendations}
        setShowSnackbar={setShowSnackbar}
        setSnackbarText={setSnackbarText}
        studentHistory={studentHistory}
        useELLDiagnostics={useELLDiagnostics}
      />
    )
  }

  const recommendationExplainer = () => {
    if (proficientOnEveryDiagnostic) {
      return (
        <React.Fragment>
          {firstName()} has demonstrated full proficiency in most skills on all diagnostics. While you can still assign a diagnostic, we recommend exploring additional content on Quill for continued growth.
          <a href="https://support.quill.org/en/articles/10751323-how-do-teachers-use-diagnostic-insights" rel="noopener noreferrer" target="_blank">Learn more</a>
        </React.Fragment>
      )
    }

    return (
      <React.Fragment>
        A student is recommended the next diagnostic after demonstrating full proficiency in most skills on the previous Pre or Post diagnostic
        <Tooltip
          tooltipText="Students qualify for the next diagnostic by demonstrating full proficiency (answering all questions correctly) in at least 80% of skills on the previous diagnostic level (Starter, Intermediate, Advanced). For example, on a 5-skill diagnostic, this means demonstrating full proficiency on 4 skills."
          tooltipTriggerText={helpImg}
        />
      </React.Fragment>
    )
  }

  const renderDiagnosticCard = (studentLearningSequenceId, diagnostics, priorDiagnostics=null) => {
    return (
      <IndividualStudentDiagnosticCard
        diagnostic={diagnostics ? diagnostics[0] : null}
        priorDiagnostic={priorDiagnostics ? priorDiagnostics[0] : null}
        studentLearningSequenceId={studentLearningSequenceId}
      />
    )
  }

  const renderPracticeCard = (studentLearningSequenceId, preOrPost, activities, relevantDiagnostics=null) => {
    return (
      <IndividualStudentPracticeCard
        activities={activities}
        preOrPost={preOrPost}
        relevantDiagnostic={relevantDiagnostics ? relevantDiagnostics[0] : null}
        studentLearningSequenceId={studentLearningSequenceId}
      />
    )
  }

  const renderStudentLearningSequenceRows = () => {
    const sequencesWithLevel = []

    DIAGNOSTIC_LEVELS.forEach(diagnosticLevel => {
      studentHistory.student_learning_sequences[diagnosticLevel]?.forEach(sequence => {
        const preDiagnosticActivities = sequence.student_learning_sequence_activities.pre_diagnostic

        if (preDiagnosticActivities) {
          sequencesWithLevel.push({
            studentLearningSequence: sequence,
            diagnosticLevel,
            preDiagnosticCompletedAt: preDiagnosticActivities[0].completed_at
          })
        }
      })
    })

    return sequencesWithLevel.sort((a, b,) => {
      return a.preDiagnosticCompletedAt > b.preDiagnosticCompletedAt ? -1 : 1
    }).map((levelledLearningSequence) => {
      const { studentLearningSequence, diagnosticLevel, } = levelledLearningSequence
      const { id, classroom_id, student_learning_sequence_activities, initial_activity_id, } = studentLearningSequence
      const { pre_diagnostic, post_diagnostic, } = student_learning_sequence_activities
      const { pre_practice, post_practice, } = sequencePractice[id] || {}

      const diagnosticName = (
        diagnosticTypeOptions.find(diagnostic => diagnostic.value === initial_activity_id)?.label ||
        `${useELLDiagnostics ? 'ELL ' : ''}${titleCase(diagnosticLevel)} Diagnostic`
      )

      return (
        <section aria-labelledby={`studentLearningSequence${id}Header`} className="student-learning-sequence" key={id}>
          <div className="top-row">
            <h4 id={`studentLearningSequence${id}Header`}>{diagnosticName}</h4>
            {teacherClassroomIds.includes(classroom_id) ? 'Assigned by you' : 'Assigned by another teacher'}
          </div>
          <div className="student-learning-sequence-cards">
            {renderDiagnosticCard(id, pre_diagnostic)}
            {renderPracticeCard(id, 'pre', pre_practice, pre_diagnostic)}
            {renderDiagnosticCard(id, post_diagnostic, pre_diagnostic)}
            {renderPracticeCard(id, 'post', post_practice, post_diagnostic)}
          </div>
        </section>
      )
    })
  }

  const renderStudentHistorySection = () => {
    if (!studentHistoryHasPreDiagnostic(studentHistory)) { return null }

    return (
      <section aria-labelledby="diagnosticHistoryHeader" className="individual-student-history">
        <h2 id="diagnosticHistoryHeader">
          <span>
            {clockIcon}
            Diagnostic History
          </span>
          <span className="timeframe">{timeframe}</span>
        </h2>
        <div className="student-learning-sequences-wrapper">
          {renderStudentLearningSequenceRows()}
        </div>
      </section>
    )
  }

  return (
    <section className="diagnostic-insights-content history-and-recommendations by-student">
      <Snackbar text={snackbarText} visible={showSnackbar} />
      <section aria-labelledby="diagnosticRecommendationHeader" className="individual-student-recommendations">
        <h2 id="diagnosticRecommendationHeader">
          <span>
            {starIcon}
            Diagnostic Recommendation
          </span>
          <span className="timeframe">{timeframe}</span>
        </h2>
        <section className="individual-student-recommendations-container">
          <div className="top-line">
            <p>{recommendationExplainer()}</p>
            <div>
              <span>Was this recommendation helpful?</span>
              <a aria-label="Submit feedback about this recommendation" className="quill-button outlined small grey icon focus-on-light" href="https://docs.google.com/forms/d/e/1FAIpQLSfMy_bxKwd8ZJ42-kuVmaUYDdj-TIjsYlrskHoSxJ0UeBZoZA/viewform?usp=header" rel="noopener noreferrer" target="_blank">
                {thumbsUpIcon}
                {thumbsDownIcon}
              </a>
            </div>
          </div>
          <div className="recommendations-wrapper">
            {DIAGNOSTIC_LEVELS.map(diagnosticLevel => renderRecommendationCard(diagnosticLevel))}
          </div>
        </section>
      </section>
      {renderStudentHistorySection()}
    </section>
  )
}

export default IndividualStudent
